import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { updateFilter } from "../redux/players/playersSlice";
import { PlayerRole } from "../redux/players/playersSlice";
import { AppDispatch } from "../redux/store";

interface UseUrlFiltersProps {
  playerRoles: PlayerRole[];
  playerFilters: any[];
  onPlayerRoleChange: (role: PlayerRole | undefined) => void;
}

export const useUrlFilters = ({
  playerRoles,
  playerFilters,
  onPlayerRoleChange,
}: UseUrlFiltersProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasInitializedFromUrl, setHasInitializedFromUrl] = useState(false);
  const [playerRolesReady, setPlayerRolesReady] = useState(false);
  const initialPlayerFiltersRef = useRef(playerFilters);
  const onPlayerRoleChangeRef = useRef(onPlayerRoleChange);
  const playerRoleFromUrlRef = useRef<PlayerRole | undefined>(undefined);

  // Update the refs when props change
  useEffect(() => {
    initialPlayerFiltersRef.current = playerFilters;
    onPlayerRoleChangeRef.current = onPlayerRoleChange;
  }, [playerFilters, onPlayerRoleChange]);

  // Initialize filters from URL on component mount (excluding playerRole)
  useEffect(() => {
    if (hasInitializedFromUrl) return;

    const urlOwnedOrEvolved = searchParams.get("ownedOrEvolved");

    // Set owned/evolved filter from URL
    if (urlOwnedOrEvolved) {
      const filterValue = urlOwnedOrEvolved === "Owned" ? 0 : 1;
      dispatch(
        updateFilter({
          key: "ownedOrEvolved",
          value: filterValue,
        }),
      );
    }

    // Set other filters from URL
    initialPlayerFiltersRef.current.forEach((filter) => {
      if (filter.type === "RANGE_FILTER") {
        const minValue = searchParams.get(`${filter.key}Min`);
        const maxValue = searchParams.get(`${filter.key}Max`);

        if (minValue || maxValue) {
          if (minValue) {
            dispatch(
              updateFilter({
                key: filter.key,
                min: parseInt(minValue),
              }),
            );
          }
          if (maxValue) {
            dispatch(
              updateFilter({
                key: filter.key,
                max: parseInt(maxValue),
              }),
            );
          }
        }
      } else if (
        filter.type === "SELECTOR_FILTER" &&
        filter.key !== "ownedOrEvolved"
      ) {
        const urlValue = searchParams.get(filter.key);
        if (urlValue) {
          const values = urlValue.split(",");
          values.forEach((value) => {
            if (filter.useLabelAsId) {
              const option = filter.options.find((o) => o.label === value);
              if (option) {
                dispatch(
                  updateFilter({
                    key: filter.key,
                    value: option.id,
                  }),
                );
              }
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue)) {
                dispatch(
                  updateFilter({
                    key: filter.key,
                    value: numValue,
                  }),
                );
              }
            }
          });
        }
      }
    });

    setHasInitializedFromUrl(true);
  }, [searchParams, dispatch, hasInitializedFromUrl]);

  // Handle playerRole initialization when playerRoles become available
  useEffect(() => {
    if (playerRoles.length > 0) {
      const urlPlayerRoleId = searchParams.get("playerRoleId");
      if (urlPlayerRoleId) {
        const role = playerRoles.find(
          (r) => r.id === parseInt(urlPlayerRoleId),
        );
        if (role) {
          playerRoleFromUrlRef.current = role;
          onPlayerRoleChangeRef.current(role);
        }
      }
      setPlayerRolesReady(true);
    }
  }, [playerRoles, searchParams]);

  // Update URL when filters change
  const updateUrlFromFilters = useCallback(
    (
      playerRole: PlayerRole | "any",
      ownedOrEvolvedFilters: any,
      stableQueryParams: any[],
    ) => {
      if (!hasInitializedFromUrl || !playerRolesReady) return;

      const newSearchParams = new URLSearchParams();
      // Add player role to URL
      if (playerRole === "any") {
        newSearchParams.delete("playerRoleId");
      } else if (playerRole?.id) {
        newSearchParams.set("playerRoleId", playerRole.id.toString());
      } else if (playerRoleFromUrlRef.current?.id) {
        // If playerRole is not set yet but we have one from URL, use that
        newSearchParams.set(
          "playerRoleId",
          playerRoleFromUrlRef.current.id.toString(),
        );
      }

      // Add owned/evolved filter to URL
      if (ownedOrEvolvedFilters?.category) {
        newSearchParams.set("ownedOrEvolved", ownedOrEvolvedFilters.category);
      }

      // Add other filters to URL
      stableQueryParams.forEach((param) => {
        const key = Object.keys(param)[0];
        const values = param[key];
        if (values && values.length > 0) {
          newSearchParams.set(key, values.join(","));
        }
      });

      // Update URL without triggering navigation
      setSearchParams(newSearchParams, { replace: true });
    },
    [hasInitializedFromUrl, playerRolesReady, setSearchParams],
  );

  return {
    hasInitializedFromUrl,
    playerRolesReady,
    updateUrlFromFilters,
  };
};
